import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, RotateCcw, ArrowUpRight, Crown, AlertTriangle } from "lucide-react";
import { Markdown } from "@/components/Markdown";
import { TrustFooter } from "@/components/TrustFooter";
import { ActionCards } from "@/components/ActionCards";
import { SourceImages } from "@/components/SourceImages";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { PLANS } from "@/lib/plans";
import { openLimitModal } from "@/lib/api";
import { SpeakButton } from "@/components/SpeakButton";

export function ChatThread({ messages, onSaveMessage, isAuthed, followups = [], onFollowup, onRetry, streaming, voiceMode = false }) {
  const { t } = useLanguage();
  const [savedKeys, setSavedKeys] = useState(() => new Set());

  const handleSave = async (msg, idx) => {
    const ok = await onSaveMessage?.(msg);
    if (ok) setSavedKeys((prev) => new Set(prev).add(idx));
  };

  const lastIsAssistant = messages.length > 0 && messages[messages.length - 1].role === "assistant";
  const showFollowups = !streaming && lastIsAssistant && followups.length > 0 && !messages[messages.length - 1].error;

  // ---- Auto-scroll behaviour (shared by the Hero chat and the full /chat page) ----
  const endRef = useRef(null);
  const stickRef = useRef(true);       // follow the stream only while user is near the bottom
  const prevUserCount = useRef(0);
  const didMountScroll = useRef(false);

  const userCount = messages.reduce((n, m) => (m.role === "user" ? n + 1 : n), 0);
  const lastMsg = messages[messages.length - 1];
  const streamingText = lastMsg && lastMsg.role === "assistant" ? lastMsg.content : "";

  const scrollToEnd = (behavior) => {
    requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior, block: "end" }));
  };

  // Stop auto-following when the user scrolls up to read older messages; resume near the bottom.
  useEffect(() => {
    const onScroll = () => {
      stickRef.current = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 140;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On first render with messages (existing conversation / redirect into chat): jump to latest.
  useEffect(() => {
    if (!didMountScroll.current && messages.length > 0) {
      didMountScroll.current = true;
      prevUserCount.current = userCount;
      stickRef.current = true;
      scrollToEnd("auto");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A new user turn (fresh question, follow-up, or auto-prompt redirect): always bring the
  // newly submitted message and its loading/response area into view.
  useEffect(() => {
    if (userCount > prevUserCount.current) {
      prevUserCount.current = userCount;
      stickRef.current = true;
      scrollToEnd("smooth");
    }
  }, [userCount]);

  // Keep the latest text in view as the response streams — but only if the user hasn't
  // scrolled up to read something older (no force-scroll when reading history).
  useEffect(() => {
    if (streaming && stickRef.current) scrollToEnd("auto");
  }, [streamingText, streaming]);

  return (
    <div className="space-y-4" data-testid="chat-thread">
      {messages.map((m, idx) =>
        m.role === "user" ? (
          <div key={idx} className="flex justify-end" data-testid="chat-user-msg">
            <div className="max-w-[85%] overflow-hidden rounded-2xl rounded-br-md bg-slate-900 text-left text-sm text-white">
              {Array.isArray(m.images) && m.images.length > 0 ? (
                <div className={`grid gap-0.5 ${m.images.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {m.images.map((src, i) => (
                    <img key={i} src={src} alt="attachment" className="max-h-56 w-full object-cover" data-testid="chat-user-image" />
                  ))}
                </div>
              ) : m.image ? (
                <img src={m.image} alt="attachment" className="max-h-56 w-full object-cover" data-testid="chat-user-image" />
              ) : null}
              {m.content && <div className="px-4 py-2.5">{m.content}</div>}
              {!m.content && (m.image || (Array.isArray(m.images) && m.images.length > 0)) && <div className="px-4 py-2 text-xs text-white/60">📷</div>}
            </div>
          </div>
        ) : (
          <div key={idx} className="flex justify-start" data-testid="chat-assistant-msg">
            <div className="w-full max-w-full rounded-2xl rounded-bl-md border border-slate-200 bg-white p-4 text-left shadow-sm">
              <div className="mb-1 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
                  <Sparkles className="h-3.5 w-3.5" /> EVERMIN
                </div>
                {m.content && !m.streaming && !m.error && (
                  <SpeakButton text={m.content} autoPlay={voiceMode && idx === messages.length - 1} />
                )}
              </div>
              {m.content ? (
                <Markdown content={m.content} />
              ) : m.streaming ? (
                m.researching ? <ResearchingIndicator sensitive={m.researchSensitive} /> : <TypingDots />
              ) : m.error ? (
                <ChatError info={m.errorInfo} onRetry={onRetry} />
              ) : null}
              {m.streaming && m.content && <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-primary/60 align-middle" />}
              {!m.streaming && !m.error && m.metadata && (
                <>
                  <SourceImages images={m.metadata.images} />
                  <ActionCards actions={m.metadata.actions} />
                  <TrustFooter
                    metadata={m.metadata}
                    isAuthed={isAuthed}
                    saved={savedKeys.has(idx)}
                    onSave={() => handleSave(m, idx)}
                  />
                </>
              )}
            </div>
          </div>
        )
      )}

      {showFollowups && (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="followup-suggestions">
          {followups.map((f, i) => (
            <button
              key={i}
              onClick={() => onFollowup?.(f)}
              data-testid={`followup-${i}`}
              className="group inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-left text-sm text-slate-700 transition-colors hover:border-primary/40 hover:text-primary"
            >
              {f}
              <ArrowUpRight className="h-3.5 w-3.5 text-slate-300 transition-colors group-hover:text-primary" />
            </button>
          ))}
        </div>
      )}

      <div ref={endRef} aria-hidden="true" className="h-px w-full scroll-mt-24" data-testid="chat-scroll-anchor" />
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-1" data-testid="chat-typing">
      {[0, 1, 2].map((i) => (
        <span key={i} className="h-2 w-2 animate-bounce rounded-full bg-slate-300" style={{ animationDelay: `${i * 120}ms` }} />
      ))}
    </div>
  );
}

function ResearchingIndicator({ sensitive }) {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-2 py-1 text-sm text-slate-500" data-testid="chat-researching">
      <span className="relative flex h-4 w-4 flex-shrink-0 items-center justify-center">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/40" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
      <span>{sensitive ? t("researching_deep") : t("researching")}</span>
    </div>
  );
}

// Classified inline error: quota exhausted → upgrade card; file too large /
// unsupported → clear reason; temporary/provider → retry. Never a bare
// "we couldn't complete that" for every failure.
function ChatError({ info, onRetry }) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const kind = info?.kind || "temporary";

  if (kind === "limit") {
    const tier = info.tier || "free";
    const plan = PLANS[tier] || PLANS.free;
    const planName = plan.name[language] || plan.name.en;
    const fill = (s) => s.replace("{count}", plan.ai).replace(/\{plan\}/g, planName);
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="chat-error-limit">
        <div className="flex items-start gap-2.5">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-400/30 text-amber-700"><Crown className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="font-display text-sm font-semibold text-slate-900">{fill(t("err_limit_title"))}</p>
            <p className="mt-0.5 text-sm text-slate-600">{fill(t("err_limit_desc"))}</p>
          </div>
        </div>
        <Button onClick={() => openLimitModal(tier)} data-testid="chat-error-upgrade-btn"
          className="mt-3 w-full gap-1.5 rounded-xl bg-amber-400 font-semibold text-slate-900 hover:bg-amber-300 sm:w-auto">
          <Sparkles className="h-4 w-4" /> {t("err_upgrade_cta")}
        </Button>
      </div>
    );
  }

  if (kind === "too_large" || kind === "unsupported") {
    const msg = kind === "too_large" ? t("err_too_large") : (info?.detail || t("err_unsupported"));
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="chat-error-file">
        <p className="flex items-start gap-2 text-sm text-rose-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" /> {msg}
        </p>
      </div>
    );
  }

  if (kind === "rate_limit") {
    return (
      <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4" data-testid="chat-error-rate">
        <p className="font-display text-sm font-semibold text-slate-900">{t("err_rate_title")}</p>
        <p className="mt-0.5 text-sm text-slate-600">{t("err_rate_desc")}</p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} data-testid="chat-retry-btn" className="mt-2 gap-1.5 rounded-full">
            <RotateCcw className="h-3.5 w-3.5" /> {t("retry")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="chat-error">
      <p className="text-sm text-rose-600">{t("err_temporary")}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} data-testid="chat-retry-btn" className="mt-2 gap-1.5 rounded-full">
          <RotateCcw className="h-3.5 w-3.5" /> {t("retry")}
        </Button>
      )}
    </div>
  );
}
